import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL;

export interface AdminCarrierCustomerTier {
  id: string;
  carrierId: string;
  name: string;
  normalizedName?: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
  _count?: { connections: number };
}

export type AdminCarrierCustomerConnectionStatus = "ACTIVE" | "BLOCKED";

export interface AdminCarrierCustomerConnection {
  id: string;
  customerId: string;
  carrierId: string;
  status: AdminCarrierCustomerConnectionStatus;
  blockedReason: string | null;
  blockedAt: string | null;
  customerRemovedAt: string | null;
  /** EZX revoked this pair — a different actor from the carrier's block above. */
  adminBlockedAt: string | null;
  adminBlockedReason: string | null;
  adminBlocked: boolean;
  addedAt: string;
  tierId: string | null;
  customer: {
    id: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  tier: AdminCarrierCustomerTier | null;
  removed: boolean;
  effectiveDiscountPercentage: number | null;
}

export interface PatchAdminCarrierCustomerVars {
  carrierId: string;
  customerId: string;
  tierId?: string | null;
  /** The CARRIER refusing this customer. */
  block?: {
    blocked: boolean;
    reason?: string | null;
  };
  /**
   * EZX revoking this pair. Deliberately separate from `block`: lifting the
   * carrier's block must not lift EZX's, and vice versa.
   */
  adminBlock?: {
    blocked: boolean;
    reason?: string | null;
  };
}

export interface AdminCarrierCustomerTierInput {
  carrierId: string;
  name: string;
  sortOrder?: number;
}

const customersKey = (carrierId: string) => [
  "admin-carrier-discounts",
  carrierId,
  "customers",
];

const tiersKey = (carrierId: string) => [
  "admin-carrier-discounts",
  carrierId,
  "tiers",
];

function authHeaders(token: string | null | undefined) {
  return {
    Authorization: `Bearer ${token}`,
  };
}

export function useAdminCarrierCustomers(carrierId: string | null) {
  const { token } = useSession();
  return useQuery({
    queryKey: customersKey(carrierId ?? ""),
    queryFn: async () => {
      const { data } = await axios.get<{
        data: AdminCarrierCustomerConnection[];
      }>(`${API_URL}/api/v2/admin/carriers/${carrierId}/customers`, {
        headers: authHeaders(token),
      });
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

export function useAdminCarrierCustomerTiers(carrierId: string | null) {
  const { token } = useSession();
  return useQuery({
    queryKey: tiersKey(carrierId ?? ""),
    queryFn: async () => {
      const { data } = await axios.get<{ data: AdminCarrierCustomerTier[] }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/tiers`,
        {
          headers: authHeaders(token),
        }
      );
      return data.data;
    },
    enabled: !!token && !!carrierId,
  });
}

export function usePatchAdminCarrierCustomer() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      customerId,
      ...body
    }: PatchAdminCarrierCustomerVars) => {
      const { data } = await axios.patch<{ ok: true }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/customers/${customerId}`,
        body,
        {
          headers: authHeaders(token),
        }
      );
      return data;
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: customersKey(vars.carrierId),
      });
      await queryClient.invalidateQueries({
        queryKey: tiersKey(vars.carrierId),
      });
    },
  });
}

export function useCreateAdminCarrierCustomerTier() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      ...body
    }: AdminCarrierCustomerTierInput) => {
      const { data } = await axios.post<{ data: AdminCarrierCustomerTier }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/tiers`,
        body,
        {
          headers: authHeaders(token),
        }
      );
      return data.data;
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: tiersKey(vars.carrierId),
      });
    },
  });
}

export function useUpdateAdminCarrierCustomerTier() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      tierId,
      ...body
    }: AdminCarrierCustomerTierInput & { tierId: string }) => {
      const { data } = await axios.patch<{ data: AdminCarrierCustomerTier }>(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/tiers/${tierId}`,
        body,
        {
          headers: authHeaders(token),
        }
      );
      return data.data;
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: tiersKey(vars.carrierId),
      });
      await queryClient.invalidateQueries({
        queryKey: customersKey(vars.carrierId),
      });
    },
  });
}

export function useDeleteAdminCarrierCustomerTier() {
  const { token } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      carrierId,
      tierId,
    }: {
      carrierId: string;
      tierId: string;
    }) => {
      await axios.delete(
        `${API_URL}/api/v2/admin/carriers/${carrierId}/tiers/${tierId}`,
        {
          headers: authHeaders(token),
        }
      );
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: tiersKey(vars.carrierId),
      });
      await queryClient.invalidateQueries({
        queryKey: customersKey(vars.carrierId),
      });
    },
  });
}
